import { notify } from "@/lib/dialog";
import { useAuthStore } from "@/state/auth-store";
import i18n from "@/i18n";

import { getPaymentProvider } from "./index";

/**
 * Recovering a store subscription the account cannot see.
 *
 * ## The problem
 *
 * An App Store / Play subscription belongs to the STORE account, not to our
 * user row. Delete an opncue account and rejoin and the new user id — which is
 * the RevenueCat `appUserID` — has nothing linked to it, while the
 * subscription is still live and still billing. The app then shows a paying
 * customer "Free" and an Upgrade button.
 *
 * ## Why the repair is always a tap
 *
 * Only `restorePurchases()` can find it. The SDK's own words:
 *
 *   restorePurchases() "Restores a user's previous purchases and links their
 *                       appUserIDs to any user's also using those purchases."
 *   getCustomerInfo()  "Gets current customer info."
 *   syncPurchases()    "should only be called if you're not calling
 *                       purchaseProduct/purchaseStoreProduct/purchasePackage"
 *                       — and we call those.
 *
 * So a read cannot see a subscription linked to a DELETED account's app user
 * id. Restore is the only call that can, and it is a MUTATION: it links, and
 * can move, purchases between accounts. RevenueCat are explicit that it be
 * triggered by user interaction, and on StoreKit 1 it can raise an OS account
 * prompt — a fallback this code cannot rule out, because `STOREKIT_VERSION`
 * is left on DEFAULT, which only selects StoreKit 2 when an In-App Purchase
 * key is configured on a dashboard this code cannot read.
 *
 * Two earlier versions ran it without a tap — once on screen mount, once when
 * the paywall was opened — and both are gone. Nothing in this module is
 * reachable except from a person pressing "Restore purchases", which both
 * billing surfaces show unconditionally.
 */

/**
 * Wait, briefly, for our own server to agree.
 *
 * The store granting an entitlement is not the user having Plus: the plan
 * flips when the RevenueCat webhook reaches POST /billing/revenuecat/webhook,
 * a second or two later. Every caller reports what THIS returns rather than
 * what the store said, so we never announce "restored" over a profile that
 * still reads Free — the exact contradiction this work exists to end.
 */
export async function waitForPlanToCatchUp(): Promise<boolean> {
  const isPlus = () => useAuthStore.getState().user?.plan === "plus";
  const { refreshUser } = useAuthStore.getState();

  await refreshUser().catch(() => {});
  for (let attempt = 0; attempt < 4 && !isPlus(); attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, 1500));
    await refreshUser().catch(() => {});
  }
  return isPlus();
}

/**
 * The manual "Restore purchases" tap, in one place because two screens offer
 * it and a divergence between them would be a divergence in what we tell
 * somebody about their own money.
 *
 * The caller owns its own busy state; this owns the store call and the copy.
 */
export async function runManualRestore(): Promise<void> {
  try {
    const result = await getPaymentProvider().restore();

    if (result.status === "success") {
      // The STORE said yes; our server is what gates the features. Reporting
      // "restored" over a profile that still reads Free would recreate the
      // exact contradiction this exists to end.
      const applied = await waitForPlanToCatchUp();
      notify(
        i18n.t(
          applied
            ? "billing.pricing.restoredTitle"
            : "billing.pricing.restorePendingTitle",
        ),
        i18n.t(
          applied
            ? "billing.pricing.restoredBody"
            : "billing.pricing.restorePendingBody",
        ),
      );
      return;
    }

    if (result.status === "nothing_to_restore") {
      // Not a failure, and it must not read as one: the person asked a
      // question about their own purchase history and this is the answer.
      notify(
        i18n.t("billing.pricing.nothingToRestoreTitle"),
        i18n.t("billing.pricing.nothingToRestoreBody"),
      );
      return;
    }

    notify(
      i18n.t("billing.pricing.paymentError"),
      result.message ?? i18n.t("billing.common.tryAgainLater"),
    );
  } catch {
    notify(
      i18n.t("billing.pricing.paymentError"),
      i18n.t("billing.common.tryAgainLater"),
    );
  }
}
